import { ANIMAL_NAMES, type Biome, type Notice, type SeedKind, type TaskType } from '@thicket/core';
import { Store, type GameStore, type StoreDeps } from '../store.js';
import { treeName } from './names.js';

/** The five bottom tabs, plus the two pages under More. */
export type Tab = 'habits' | 'dailies' | 'todos' | 'garden' | 'more' | 'journal' | 'settings';

export type Sheet =
  | { kind: 'task'; taskId: string | null; newType?: TaskType }
  | { kind: 'bloom'; plantId: string }
  | { kind: 'pause' };

/** What the garden is waiting for the next tap to do. */
export type Planting =
  | { kind: 'plant'; plantId: string; label: string }
  | { kind: 'seed'; seed: SeedKind };

export const BIOME_NAMES: Record<Biome, string> = {
  meadow: 'Meadow', wetland: 'Wetland', jungleRuins: 'Jungle ruins', canyon: 'Canyon', pineRidge: 'Pine ridge',
  woodlandLake: 'Woodland lake', coast: 'Coast', mountainSummit: 'Mountain summit', sacredGrove: 'Sacred grove',
};

export const SEED_NAMES: Record<SeedKind, string> = { clover: 'clover', daisy: 'daisy', tulip: 'tulip', sunflower: 'sunflower' };

let rerender: () => void = () => {};
let uid = 0;

/** What a store needs from the browser; dev mode's sandbox uses the same. */
export const storeDeps: StoreDeps = {
  fetch: (...args) => fetch(...args),
  storage: (() => {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  })(),
  uuid: () => crypto.randomUUID(),
  now: () => new Date(),
  onSignedOut: () => location.assign(`/signin?return_to=${encodeURIComponent(location.pathname)}`),
  onNotices: (notices) => notices.forEach((n) => toastNotice(n)),
  onMessage: (m) => toast(m),
  later: (fn, ms) => void setTimeout(fn, ms),
};

export const app = {
  store: new Store(storeDeps) as GameStore,
  tab: 'dailies' as Tab,
  sheet: null as Sheet | null,
  planting: null as Planting | null,
  biome: null as Biome | null,
  toasts: [] as { id: number; text: string }[],
  /** Dev mode (/dev): the store is a sandbox copy and the dev panel shows. */
  dev: false,
  /** This account may open dev mode (from /api/me). */
  canDev: false,
  /** The garden fills the whole display: no header, tabs or drawer. */
  immersive: false,
  /** The plant the dev panel is tuning. */
  devPick: null as string | null,
};

export function onRender(fn: () => void) {
  rerender = fn;
}

/** Changes UI state and redraws. */
export function update(fn?: () => void) {
  fn?.();
  rerender();
}

export function toast(text: string, ms = 2600) {
  const id = ++uid;
  update(() => {
    app.toasts.push({ id, text });
    // A burst of rewards shouldn't cover the screen: keep the newest three.
    if (app.toasts.length > 3) app.toasts = app.toasts.slice(-3);
  });
  setTimeout(() => update(() => (app.toasts = app.toasts.filter((t) => t.id !== id))), ms);
}

const seedText = (seeds: Partial<Record<SeedKind, number>>) =>
  Object.entries(seeds).filter(([, n]) => n! > 0).map(([k]) => ` · a ${SEED_NAMES[k as SeedKind]} seed`).join('');

/** The game's news, in Thicket's gentle voice. Streak endings are never pushed. */
export function toastNotice(n: Notice) {
  const view = app.store.view;
  switch (n.type) {
    case 'reward': {
      // After an undo, a completion may pay less: what was already counted isn't paid twice.
      const text = [n.coins ? `+${n.coins} coins` : '', n.xp ? `+${n.xp} XP` : ''].filter(Boolean).join(' · ') + seedText(n.seeds);
      return toast(text.replace(/^ · /, '') || 'Done · already counted earlier');
    }
    case 'weeklyBonus':
      return toast(`${n.streak} days of care!${n.coins ? ` +${n.coins} coins` : ''}${seedText(n.seeds)}`, 3500);
    case 'rewardReversed':
      return toast('Undone');
    case 'levelUp':
      return toast(`Level ${n.level}!`, 3500);
    case 'biomeUnlocked':
      return toast(`A new place opened: ${BIOME_NAMES[n.biome]}`, 4500);
    case 'grew': {
      const plant = view?.plants.find((p) => p.id === n.plantId);
      const what = plant?.kind === 'tree' ? `Your ${treeName(plant.species, plant.biome)}` : 'A plant';
      return toast(`${what} grew to ${n.stage}${n.stage === 'mega' ? '!' : ''}`, 3500);
    }
    case 'leavesFull':
      return toast('Its leaves filled back in');
    case 'bloomed':
      return toast('A wildflower bloomed');
    case 'picked':
      return toast(n.use === 'coins' ? `Sold for ${n.coins} coins` : n.use === 'bouquet' ? 'Added to your bouquet' : 'Pressed into your book');
    case 'weedSprouted':
      return toast('A little weed sprouted. It fades on its own.');
    case 'welcomeBack':
      return toast(
        `Welcome back!${n.wentToSeed ? ` ${n.wentToSeed} wildflower${n.wentToSeed > 1 ? 's' : ''} went to seed and left seed packets.` : ''}`,
        5000,
      );
    case 'planted':
      return toast(`Planted a ${SEED_NAMES[n.seed]}`);
    case 'animalJoined':
      return toast(`${n.name} the ${ANIMAL_NAMES[n.kind]?.toLowerCase() ?? n.kind} moved in`, 3500);
    default:
      return;
  }
}
